import {
  CheckCircle2,
  Clock3,
  LockKeyhole,
  MessageSquareText,
  RotateCcw,
} from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

export default function HomeworkPage() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [availability, setAvailability] = useState([]);
  const [selected, setSelected] = useState(null);
  const [feedback, setFeedback] = useState("");
  const [grade, setGrade] = useState(90);

  const load = async () => {
    const requests = [api.get("/homework")];
    if (user.role === "student")
      requests.push(api.get("/homework/availability"));
    const [homework, courses] = await Promise.all(requests);
    setItems(homework.data);
    setAvailability(courses?.data || []);
  };

  useEffect(() => {
    load().catch(() => toast.error("Nie udało się pobrać zadań"));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const review = async (status) => {
    await api.put(`/homework/${selected.id}/review`, {
      status,
      feedback,
      grade: Number(grade),
    });
    toast.success(
      status === "approved" ? "Praca zaakceptowana" : "Wysłano do poprawy",
    );
    setSelected(null);
    setFeedback("");
    load();
  };

  const statusLabel = {
    submitted: "OCZEKUJE",
    approved: "ZALICZONE",
    revision: "DO POPRAWY",
  };
  const lockedCourses = availability.filter((course) => !course.enabled);

  return (
    <div className="page" data-testid="homework-page">
      <div className="page-heading">
        <div>
          <span className="eyebrow">FEEDBACK LOOP</span>
          <h1 data-testid="homework-title">
            {user.role === "student" ? "Moje zadania" : "Prace do sprawdzenia"}
          </h1>
          <p>
            {user.role === "student"
              ? "Śledź status i wskazówki od kuratora."
              : "Konkretny feedback przyspiesza postępy uczniów."}
          </p>
        </div>
      </div>
      <div className="homework-list">
        {user.role === "student" &&
          lockedCourses.map((course) => (
            <article
              className="homework-course-locked"
              key={course.course_id}
              data-testid={`homework-disabled-${course.course_id}`}
            >
              <div className="homework-lock-icon">
                <LockKeyhole />
              </div>
              <div>
                <small>{course.course_title}</small>
                <h3>Zadania domowe są wyłączone</h3>
                <p>Dla tego kursu nie ma sekcji z zadaniami domowymi.</p>
              </div>
            </article>
          ))}
        {items.length === 0 && lockedCourses.length === 0 && (
          <div className="empty-list" data-testid="empty-homework-state">
            <CheckCircle2 />
            <h2>
              {user.role === "student"
                ? "Brak wysłanych zadań"
                : "Wszystko sprawdzone"}
            </h2>
            <p>
              {user.role === "student"
                ? "Twoje przesłane prace pojawią się tutaj."
                : "Nie ma teraz żadnych oczekujących prac."}
            </p>
          </div>
        )}
        {items.map((item) => (
          <article key={item.id} data-testid={`homework-item-${item.id}`}>
            <div className="homework-status">
              <Clock3 />
              <span className={item.status}>{statusLabel[item.status]}</span>
            </div>
            <div>
              <small>{item.course_title}</small>
              <h3>{item.student_name || "Twoja praca"}</h3>
              <p>{item.content}</p>
              {item.feedback && (
                <blockquote data-testid={`homework-feedback-${item.id}`}>
                  <MessageSquareText /> {item.feedback}
                  <strong>{item.grade}/100</strong>
                </blockquote>
              )}
            </div>
            {user.role !== "student" && (
              <button
                onClick={() => {
                  setSelected(item);
                  setFeedback(item.feedback || "");
                  setGrade(item.grade || 90);
                }}
                data-testid={`review-homework-${item.id}`}
              >
                SPRAWDŹ
              </button>
            )}
          </article>
        ))}
      </div>
      {selected && (
        <div className="modal-backdrop" data-testid="review-modal">
          <div className="review-modal">
            <button
              className="modal-close"
              onClick={() => setSelected(null)}
              data-testid="close-review-modal"
            >
              ×
            </button>
            <span className="eyebrow">OCENA PRACY</span>
            <h2>{selected.student_name}</h2>
            <p>{selected.content}</p>
            <label>
              INFORMACJA ZWROTNA
              <textarea
                value={feedback}
                onChange={(event) => setFeedback(event.target.value)}
                data-testid="review-feedback-input"
              />
            </label>
            <label>
              WYNIK / 100
              <input
                type="number"
                min="0"
                max="100"
                value={grade}
                onChange={(event) => setGrade(event.target.value)}
                data-testid="review-grade-input"
              />
            </label>
            <div>
              <button
                onClick={() => review("revision")}
                data-testid="request-revision-button"
              >
                <RotateCcw /> DO POPRAWY
              </button>
              <button
                className="primary-btn compact"
                onClick={() => review("approved")}
                data-testid="approve-homework-button"
              >
                <CheckCircle2 /> ZALICZ PRACĘ
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
